import React, { useState, useEffect } from 'react';
import config from '../../config';
import './SuppliersManagement.css';
import { VendorManagement } from '../VendorManagement';

interface Supplier {
  id: string;
  name: string;
  code?: string;
  contactInfo: {
    email?: string;
    phone?: string;
    address?: string;
    website?: string;
    primaryContact?: string;
  };
  paymentTerms?: string;
  deliverySchedule?: {
    days?: string[];
    cutoffTime?: string;
    leadTimeDays?: number;
  };
  minimumOrder?: number;
  deliveryFee?: number;
  freeDeliveryThreshold?: number;
  supplierType: string;
  rating?: number;
  isActive: boolean;
  createdAt: string;
  supplierItems?: any[];
}

interface SupplierItem {
  id: string;
  supplierSku: string;
  supplierName?: string;
  packSize: number;
  packUnit: string;
  costPerPack: number;
  costPerBaseUnit: number;
  isPreferred: boolean;
  item: {
    id: string;
    name: string;
    category: string;
    baseUnit: string;
  };
}

interface SuppliersManagementProps {
  selectedStore: string;
  enterpriseId: string;
  user: any;
}

const SuppliersManagement: React.FC<SuppliersManagementProps> = ({ selectedStore, enterpriseId, user }) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSupplierForm, setShowSupplierForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showVendorManagement, setShowVendorManagement] = useState(false);

  const [supplierFormData, setSupplierFormData] = useState({
    name: '',
    code: '',
    contactInfo: {
      email: '',
      phone: '',
      address: '',
      website: '',
      primaryContact: ''
    },
    paymentTerms: '',
    deliverySchedule: {
      days: [] as string[],
      cutoffTime: '',
      leadTimeDays: 1
    },
    minimumOrder: '',
    deliveryFee: '',
    freeDeliveryThreshold: '',
    supplierType: 'vendor',
    rating: ''
  });

  const [supplierItemFormData, setSupplierItemFormData] = useState({
    itemId: '',
    supplierSku: '',
    supplierName: '',
    packSize: '',
    packUnit: '',
    costPerPack: '',
    conversionFactor: '1',
    minimumOrderQuantity: '1',
    orderIncrement: '1',
    leadTimeDays: '',
    shelfLifeDays: '',
    isPreferred: false
  });

  const supplierTypes = ['vendor', 'distributor', 'manufacturer'];
  const paymentTermsOptions = ['COD', 'Net 15', 'Net 30', 'Net 45', '2/10 Net 30'];
  const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const units = ['lb', 'oz', 'kg', 'g', 'gallon', 'quart', 'liter', 'ml', 'each', 'dozen', 'case', 'box', 'bag', 'can', 'bottle'];

  useEffect(() => {
    if (enterpriseId) {
      fetchSuppliers();
      fetchItems();
    }
  }, [enterpriseId]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${config.API_BASE_URL}/inventory/suppliers/${enterpriseId}`);
      
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data);
      } else {
        console.error('Failed to fetch suppliers');
        setSuppliers([]);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/inventory/items/${enterpriseId}`);
      
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      } else {
        console.error('Failed to fetch items');
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const handleSupplierSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...supplierFormData,
        minimumOrder: supplierFormData.minimumOrder ? parseFloat(supplierFormData.minimumOrder) : null,
        deliveryFee: supplierFormData.deliveryFee ? parseFloat(supplierFormData.deliveryFee) : null,
        freeDeliveryThreshold: supplierFormData.freeDeliveryThreshold ? parseFloat(supplierFormData.freeDeliveryThreshold) : null,
        rating: supplierFormData.rating ? parseFloat(supplierFormData.rating) : null,
        enterpriseId
      };

      const url = editingSupplier 
        ? `${config.API_BASE_URL}/inventory/suppliers/${editingSupplier.id}`
        : `${config.API_BASE_URL}/inventory/suppliers`;
      
      const method = editingSupplier ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await fetchSuppliers();
        resetSupplierForm();
        setShowSupplierForm(false);
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving supplier:', error);
      alert('Error saving supplier');
    }
  };

  const handleSupplierItemSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedSupplier) return;

    try {
      const payload = {
        ...supplierItemFormData,
        packSize: parseFloat(supplierItemFormData.packSize),
        costPerPack: parseFloat(supplierItemFormData.costPerPack),
        conversionFactor: parseFloat(supplierItemFormData.conversionFactor),
        minimumOrderQuantity: parseInt(supplierItemFormData.minimumOrderQuantity),
        orderIncrement: parseInt(supplierItemFormData.orderIncrement),
        leadTimeDays: supplierItemFormData.leadTimeDays ? parseInt(supplierItemFormData.leadTimeDays) : null,
        shelfLifeDays: supplierItemFormData.shelfLifeDays ? parseInt(supplierItemFormData.shelfLifeDays) : null,
        supplierId: selectedSupplier.id
      };

      const response = await fetch(`${config.API_BASE_URL}/inventory/supplier-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        await fetchSuppliers();
        resetSupplierItemForm();
        setShowItemForm(false);
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving supplier item:', error);
      alert('Error saving supplier item');
    }
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setSupplierFormData({
      name: supplier.name,
      code: supplier.code || '',
      contactInfo: {
        email: supplier.contactInfo?.email || '',
        phone: supplier.contactInfo?.phone || '',
        address: supplier.contactInfo?.address || '',
        website: supplier.contactInfo?.website || '',
        primaryContact: supplier.contactInfo?.primaryContact || ''
      },
      paymentTerms: supplier.paymentTerms || '',
      deliverySchedule: {
        days: supplier.deliverySchedule?.days || [],
        cutoffTime: supplier.deliverySchedule?.cutoffTime || '',
        leadTimeDays: supplier.deliverySchedule?.leadTimeDays || 1
      },
      minimumOrder: supplier.minimumOrder?.toString() || '',
      deliveryFee: supplier.deliveryFee?.toString() || '',
      freeDeliveryThreshold: supplier.freeDeliveryThreshold?.toString() || '',
      supplierType: supplier.supplierType,
      rating: supplier.rating?.toString() || ''
    });
    setShowSupplierForm(true);
  };

  const resetSupplierForm = () => {
    setSupplierFormData({
      name: '',
      code: '',
      contactInfo: {
        email: '',
        phone: '',
        address: '',
        website: '',
        primaryContact: ''
      },
      paymentTerms: '',
      deliverySchedule: {
        days: [],
        cutoffTime: '',
        leadTimeDays: 1
      },
      minimumOrder: '',
      deliveryFee: '',
      freeDeliveryThreshold: '',
      supplierType: 'vendor',
      rating: ''
    });
    setEditingSupplier(null);
  };

  const resetSupplierItemForm = () => {
    setSupplierItemFormData({
      itemId: '',
      supplierSku: '',
      supplierName: '',
      packSize: '',
      packUnit: '',
      costPerPack: '',
      conversionFactor: '1',
      minimumOrderQuantity: '1',
      orderIncrement: '1',
      leadTimeDays: '',
      shelfLifeDays: '',
      isPreferred: false
    });
  };

  const handleDayToggle = (day: string) => {
    const currentDays = supplierFormData.deliverySchedule.days;
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    
    setSupplierFormData(prev => ({
      ...prev,
      deliverySchedule: { ...prev.deliverySchedule, days: newDays }
    }));
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getSupplierTypes = () => {
    const typeSet = new Set(suppliers.map(supplier => supplier.supplierType));
    const types = Array.from(typeSet);
    return types.sort();
  };

  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (supplier.code && supplier.code.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = !typeFilter || supplier.supplierType === typeFilter;
    return matchesSearch && matchesType && supplier.isActive;
  });

  if (loading) {
    return (
      <div className="suppliers-loading">
        <div className="loading-spinner"></div>
        <p>Loading suppliers...</p>
      </div>
    );
  }

  return (
    <div className="suppliers-management">
      {/* Header */}
      <div className="suppliers-header">
        <div className="header-info">
          <h2>Suppliers Management</h2>
          <p>Manage vendors, distributors, and their product offerings.</p>
        </div>
        <div className="header-actions">
          <button 
            className="vendor-management-btn" 
            onClick={() => setShowVendorManagement(true)}
          >
            Advanced Vendor Management
          </button>
          <button 
            className="add-supplier-btn" 
            onClick={() => {
              resetSupplierForm();
              setShowSupplierForm(true);
            }}
          >
            + Add New Supplier
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="suppliers-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search suppliers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Types</option>
          {getSupplierTypes().map(type => (
            <option key={type} value={type}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {/* Suppliers List */}
      <div className="suppliers-list">
        {filteredSuppliers.length === 0 ? (
          <div className="empty-suppliers">
            <h3>No Suppliers Found</h3>
            <p>Start by adding suppliers to your vendor network.</p>
            <button 
              className="add-first-supplier-btn"
              onClick={() => {
                resetSupplierForm();
                setShowSupplierForm(true);
              }}
            >
              Add Your First Supplier
            </button>
          </div>
        ) : (
          <div className="suppliers-grid">
            {filteredSuppliers.map((supplier) => (
              <div key={supplier.id} className="supplier-card">
                <div className="supplier-header">
                  <div className="supplier-main-info">
                    <h3 className="supplier-name">{supplier.name}</h3>
                    {supplier.code && (
                      <span className="supplier-code">Code: {supplier.code}</span>
                    )}
                  </div>
                  <span className={`type-badge ${supplier.supplierType}`}>
                    {supplier.supplierType}
                  </span>
                </div>

                <div className="supplier-details">
                  {supplier.contactInfo?.email && (
                    <div className="detail-row">
                      <span className="label">Email:</span>
                      <span className="value">{supplier.contactInfo.email}</span>
                    </div>
                  )}
                  
                  {supplier.contactInfo?.phone && (
                    <div className="detail-row">
                      <span className="label">Phone:</span>
                      <span className="value">{supplier.contactInfo.phone}</span>
                    </div>
                  )}
                  
                  {supplier.paymentTerms && (
                    <div className="detail-row">
                      <span className="label">Payment:</span>
                      <span className="value">{supplier.paymentTerms}</span>
                    </div>
                  )}
                  
                  {supplier.minimumOrder && (
                    <div className="detail-row">
                      <span className="label">Min Order:</span>
                      <span className="value">{formatCurrency(supplier.minimumOrder)}</span>
                    </div>
                  )}
                  
                  {supplier.deliverySchedule?.days && supplier.deliverySchedule.days.length > 0 && (
                    <div className="detail-row">
                      <span className="label">Delivery:</span>
                      <span className="value">
                        {supplier.deliverySchedule.days.map(day => 
                          day.charAt(0).toUpperCase() + day.slice(1, 3)
                        ).join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {supplier.rating && (
                    <div className="detail-row">
                      <span className="label">Rating:</span>
                      <span className="value">
                        {'★'.repeat(Math.floor(supplier.rating))} {supplier.rating.toFixed(1)}
                      </span>
                    </div>
                  )}
                  
                  {supplier.supplierItems && supplier.supplierItems.length > 0 && (
                    <div className="detail-row">
                      <span className="label">Items:</span>
                      <span className="value">{supplier.supplierItems.length} configured</span>
                    </div>
                  )}
                </div>

                <div className="supplier-actions">
                  <button 
                    className="edit-btn"
                    onClick={() => handleEditSupplier(supplier)}
                  >
                    Edit
                  </button>
                  <button 
                    className="manage-items-btn"
                    onClick={() => {
                      setSelectedSupplier(supplier);
                      resetSupplierItemForm();
                      setShowItemForm(true);
                    }}
                  >
                    Manage Items
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Supplier Form Modal */}
      {showSupplierForm && (
        <div className="form-overlay">
          <div className="form-modal">
            <div className="form-header">
              <h3>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowSupplierForm(false);
                  resetSupplierForm();
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSupplierSubmit} className="supplier-form">
              {/* Basic Information */}
              <div className="form-section">
                <h4>Basic Information</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Supplier Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={supplierFormData.name}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      placeholder="e.g., Premium Food Distributors"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="code">Supplier Code</label>
                    <input
                      type="text"
                      id="code"
                      value={supplierFormData.code}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, code: e.target.value }))}
                      placeholder="e.g., PFD001"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="supplierType">Supplier Type *</label>
                    <select
                      id="supplierType"
                      value={supplierFormData.supplierType}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, supplierType: e.target.value }))}
                      required
                    >
                      {supplierTypes.map(type => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="rating">Rating (1-5)</label>
                    <input
                      type="number"
                      id="rating"
                      value={supplierFormData.rating}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, rating: e.target.value }))}
                      min="1"
                      max="5"
                      step="0.1"
                      placeholder="4.5"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="form-section">
                <h4>Contact Information</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={supplierFormData.contactInfo.email}
                      onChange={(e) => setSupplierFormData(prev => ({
                        ...prev,
                        contactInfo: { ...prev.contactInfo, email: e.target.value }
                      }))}
                      placeholder="orders@supplier.com"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      value={supplierFormData.contactInfo.phone}
                      onChange={(e) => setSupplierFormData(prev => ({
                        ...prev,
                        contactInfo: { ...prev.contactInfo, phone: e.target.value }
                      }))}
                      placeholder="555-123-4567"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    value={supplierFormData.contactInfo.address}
                    onChange={(e) => setSupplierFormData(prev => ({
                      ...prev,
                      contactInfo: { ...prev.contactInfo, address: e.target.value }
                    }))}
                    rows={2}
                    placeholder="123 Distribution Way, Food City, FC 12345"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="website">Website</label>
                    <input
                      type="url"
                      id="website"
                      value={supplierFormData.contactInfo.website}
                      onChange={(e) => setSupplierFormData(prev => ({
                        ...prev,
                        contactInfo: { ...prev.contactInfo, website: e.target.value }
                      }))}
                      placeholder="https://supplier.com"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="primaryContact">Primary Contact</label>
                    <input
                      type="text"
                      id="primaryContact"
                      value={supplierFormData.contactInfo.primaryContact}
                      onChange={(e) => setSupplierFormData(prev => ({
                        ...prev,
                        contactInfo: { ...prev.contactInfo, primaryContact: e.target.value }
                      }))}
                      placeholder="John Smith"
                    />
                  </div>
                </div>
              </div>

              {/* Business Terms */}
              <div className="form-section">
                <h4>Business Terms</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="paymentTerms">Payment Terms</label>
                    <select
                      id="paymentTerms"
                      value={supplierFormData.paymentTerms}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                    >
                      <option value="">Select terms</option>
                      {paymentTermsOptions.map(term => (
                        <option key={term} value={term}>{term}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="leadTimeDays">Lead Time (days)</label>
                    <input
                      type="number"
                      id="leadTimeDays"
                      value={supplierFormData.deliverySchedule.leadTimeDays}
                      onChange={(e) => setSupplierFormData(prev => ({
                        ...prev,
                        deliverySchedule: { ...prev.deliverySchedule, leadTimeDays: parseInt(e.target.value) || 1 }
                      }))}
                      min="1"
                      placeholder="1"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="minimumOrder">Minimum Order ($)</label>
                    <input
                      type="number"
                      id="minimumOrder"
                      value={supplierFormData.minimumOrder}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, minimumOrder: e.target.value }))}
                      step="0.01"
                      min="0"
                      placeholder="250.00"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="deliveryFee">Delivery Fee ($)</label>
                    <input
                      type="number"
                      id="deliveryFee"
                      value={supplierFormData.deliveryFee}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, deliveryFee: e.target.value }))}
                      step="0.01"
                      min="0"
                      placeholder="25.00"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="freeDeliveryThreshold">Free Delivery Threshold ($)</label>
                    <input
                      type="number"
                      id="freeDeliveryThreshold"
                      value={supplierFormData.freeDeliveryThreshold}
                      onChange={(e) => setSupplierFormData(prev => ({ ...prev, freeDeliveryThreshold: e.target.value }))}
                      step="0.01"
                      min="0"
                      placeholder="500.00"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="cutoffTime">Order Cutoff Time</label>
                    <input
                      type="time"
                      id="cutoffTime"
                      value={supplierFormData.deliverySchedule.cutoffTime}
                      onChange={(e) => setSupplierFormData(prev => ({
                        ...prev,
                        deliverySchedule: { ...prev.deliverySchedule, cutoffTime: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </div>

              {/* Delivery Schedule */}
              <div className="form-section">
                <h4>Delivery Schedule</h4>
                <div className="delivery-days">
                  {weekDays.map(day => (
                    <label key={day} className="day-checkbox">
                      <input
                        type="checkbox"
                        checked={supplierFormData.deliverySchedule.days.includes(day)}
                        onChange={() => handleDayToggle(day)}
                      />
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setShowSupplierForm(false);
                    resetSupplierForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingSupplier ? 'Update Supplier' : 'Create Supplier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Supplier Item Form Modal */}
      {showItemForm && selectedSupplier && (
        <div className="form-overlay">
          <div className="form-modal">
            <div className="form-header">
              <h3>Add Item for {selectedSupplier.name}</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowItemForm(false);
                  resetSupplierItemForm();
                  setSelectedSupplier(null);
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSupplierItemSubmit} className="supplier-item-form">
              <div className="form-section">
                <h4>Item Information</h4>
                
                <div className="form-group">
                  <label htmlFor="itemId">Select Item *</label>
                  <select
                    id="itemId"
                    value={supplierItemFormData.itemId}
                    onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, itemId: e.target.value }))}
                    required
                  >
                    <option value="">Choose an item</option>
                    {items.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name} ({item.category})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="supplierSku">Supplier SKU *</label>
                    <input
                      type="text"
                      id="supplierSku"
                      value={supplierItemFormData.supplierSku}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, supplierSku: e.target.value }))}
                      required
                      placeholder="e.g., BEEF-80-20-40LB"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="supplierName">Supplier Item Name</label>
                    <input
                      type="text"
                      id="supplierName"
                      value={supplierItemFormData.supplierName}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, supplierName: e.target.value }))}
                      placeholder="Ground Beef 80/20 Case"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="packSize">Pack Size *</label>
                    <input
                      type="number"
                      id="packSize"
                      value={supplierItemFormData.packSize}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, packSize: e.target.value }))}
                      required
                      step="0.001"
                      min="0"
                      placeholder="40"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="packUnit">Pack Unit *</label>
                    <select
                      id="packUnit"
                      value={supplierItemFormData.packUnit}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, packUnit: e.target.value }))}
                      required
                    >
                      <option value="">Select unit</option>
                      {units.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="costPerPack">Cost Per Pack *</label>
                    <input
                      type="number"
                      id="costPerPack"
                      value={supplierItemFormData.costPerPack}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, costPerPack: e.target.value }))}
                      required
                      step="0.01"
                      min="0"
                      placeholder="120.00"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="conversionFactor">Conversion Factor</label>
                    <input
                      type="number"
                      id="conversionFactor"
                      value={supplierItemFormData.conversionFactor}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, conversionFactor: e.target.value }))}
                      step="0.000001"
                      min="0"
                      placeholder="1"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="minimumOrderQuantity">Min Order Qty</label>
                    <input
                      type="number"
                      id="minimumOrderQuantity"
                      value={supplierItemFormData.minimumOrderQuantity}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, minimumOrderQuantity: e.target.value }))}
                      min="1"
                      placeholder="1"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="orderIncrement">Order Increment</label>
                    <input
                      type="number"
                      id="orderIncrement"
                      value={supplierItemFormData.orderIncrement}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, orderIncrement: e.target.value }))}
                      min="1"
                      placeholder="1"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="leadTimeDays">Lead Time (days)</label>
                    <input
                      type="number"
                      id="leadTimeDays"
                      value={supplierItemFormData.leadTimeDays}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, leadTimeDays: e.target.value }))}
                      min="1"
                      placeholder="2"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="shelfLifeDays">Shelf Life (days)</label>
                    <input
                      type="number"
                      id="shelfLifeDays"
                      value={supplierItemFormData.shelfLifeDays}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, shelfLifeDays: e.target.value }))}
                      min="1"
                      placeholder="3"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={supplierItemFormData.isPreferred}
                      onChange={(e) => setSupplierItemFormData(prev => ({ ...prev, isPreferred: e.target.checked }))}
                    />
                    Preferred supplier for this item
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setShowItemForm(false);
                    resetSupplierItemForm();
                    setSelectedSupplier(null);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  Add Supplier Item
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Advanced Vendor Management Modal */}
      {showVendorManagement && (
        <div className="modal-overlay" onClick={() => setShowVendorManagement(false)}>
          <div className="vendor-management-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Advanced Vendor Management</h2>
              <button 
                className="close-btn"
                onClick={() => setShowVendorManagement(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <VendorManagement enterpriseId={enterpriseId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersManagement;